import { useState } from 'react';
import { useT } from '@/store';
import {
  useArchiveClient, useClients, useCreateClient, useIssueMagicLink,
  useRestoreClient, useRevokeClientSessions, useUpdateClient,
} from '@/hooks/useClientRequests';
import { EmptyState, ErrorState, Panel, SkeletonRows } from '@/components/ui/Panel';
import { Modal } from '@/components/ui/Overlay';
import { PaginationFooter, SearchInput } from '@/components/ui/TableToolbar';
import { useOffsetClamp, usePagedList } from '@/hooks/usePagedList';
import { SmartImportModal } from '@/components/data/SmartImportModal';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { ArchivedBadge } from '@/components/ui/ArchivedBadge';
import { AddressSearchInput } from '@/components/ui/AddressSearchInput';
import { ClientAccessModal } from './ClientAccessModal';

// Transporto kainodaros modeliai: purely informational grouping, no
// automated pricing consequence — see schema.sql's comment on
// clients.segment. Kept here rather than a shared constants file since
// nothing else in the console needs this list yet — not exported, since
// this file's other export is the ClientList component itself (Fast
// Refresh only works when a file exports components alone).
const CLIENT_SEGMENTS = ['transport', 'farmer', 'forestry', 'other'];

const EMPTY_FORM = {
  client_name: '', registration_code: '', vat_code: '', address: '',
  contact_email: '', contact_phone: '', dwell_free_minutes_site: '', segment: '',
};

/**
 * Klientai — the B2B client companies orders are placed for.
 *
 * This table did not exist. `useClients()` had been written and was called
 * by nothing: clients could only be created as a side effect of approving a
 * portal request, and the ones already in the table were invisible in the
 * console. Which is also why the bulk importer matters most here — a fleet's
 * client list is the biggest of the three, and the only one that arrives as
 * a spreadsheet from an accountant.
 *
 * Rows rather than cards, unlike the approvals queue next door: a client is
 * scanned (do we have their code? is anything pending?), not read.
 */
export function ClientList() {
  const t = useT();
  const { search, onSearchChange, limit, setLimit, offset, setOffset } = usePagedList();
  // V-71. Archived clients had no screen at all: nothing in frontend/src
  // ever asked for include_inactive, so a client archived by any means was
  // invisible here and there was no way back. Three of them were sitting in
  // the database this was measured against.
  const [showArchived, setShowArchived] = useState(false);
  const [segmentFilter, setSegmentFilter] = useState('');
  const { data: clients = [], totalCount, isLoading, isError, error } =
    useClients({ search, limit, offset, includeInactive: showArchived, segment: segmentFilter });
  // V-296. Archiving the last client on a later page shrinks totalCount
  // without ever touching offset — without this, the list reads a false
  // "no clients" over a book that still has rows one page back.
  useOffsetClamp(totalCount, limit, offset, setOffset);

  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const archiveClient = useArchiveClient();
  const restoreClient = useRestoreClient();
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  // Letting a client into the portal was unreachable from anywhere in the
  // console until this: useIssueMagicLink() existed and nothing called it.
  const [issuingFor, setIssuingFor] = useState(null);
  const issueLink = useIssueMagicLink();
  // The other half of the same subject, and the other hook nothing called.
  const revokeSessions = useRevokeClientSessions();

  const saving = createClient.isPending || updateClient.isPending;
  const saveError = createClient.error ?? updateClient.error;
  const [formError, setFormError] = useState(null);

  const submit = () => {
    const name = editing.client_name.trim();
    // V-47, the fourth of them, and this is the first dialog on the tab —
    // the one a browser walkthrough opened first. A bare `return` here left
    // Išsaugoti doing nothing and saying nothing.
    if (!name) return setFormError('clients.errName');
    setFormError(null);
    const body = {
      client_name: name,
      registration_code: editing.registration_code.trim() || null,
      // V-73's neighbour on the sell side. The BUYER's VAT code and
      // registered address are invoice requisites and no screen could enter
      // either: the console's create and update sent four fields, and the
      // two missing ones are the two core/pdf_export.py then reports as
      // absent on the document itself — in a note telling the carrier to
      // "įrašykite juos kliento kortelėje", on a card that had no such
      // boxes. Both routes have accepted them all along
      // (_buyer_identity_fields in blueprints/clients.py).
      vat_code: editing.vat_code.trim() || null,
      address: editing.address.trim() || null,
      contact_email: editing.contact_email.trim() || null,
      contact_phone: editing.contact_phone.trim() || null,
      segment: editing.segment || null,
    };
    const done = { onSuccess: () => setEditing(null) };
    if (editing.clientId) {
      // Only on update: POST /api/tenants/<id>/clients does not accept it,
      // and a field the backend silently drops is worse than one that is
      // not offered. Empty clears the term — it is not a zero, which is
      // itself a term (billing from the first minute).
      body.dwell_free_minutes_site =
        editing.dwell_free_minutes_site === '' ? null : Number(editing.dwell_free_minutes_site);
      updateClient.mutate({ clientId: editing.clientId, ...body }, done);
    }
    else createClient.mutate(body, done);
  };

  return (
    <Panel
      title={t('clients.title')}
      actions={
        <>
          <SearchInput
            value={search}
            onDebouncedChange={onSearchChange}
            placeholder={t('clients.searchPh')}
          />
          <label className="flex items-center gap-1.5 text-[11px] text-dim">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                // Same reason usePagedList resets it on a search: this
                // changes how many rows there are, and asking the server for
                // rows 76-100 of a shorter list returns an empty table for a
                // filter that plainly matches something.
                setOffset(0);
              }}
            />
            {t('common.showArchived')}
          </label>
          <select
            className="field !w-auto !py-1 !text-[11px]"
            value={segmentFilter}
            onChange={(e) => { setSegmentFilter(e.target.value); setOffset(0); }}
          >
            <option value="">{t('clients.segmentFilterAll')}</option>
            {CLIENT_SEGMENTS.map((segment) => (
              <option key={segment} value={segment}>{t(`clients.segment.${segment}`)}</option>
            ))}
          </select>
          <button type="button" className="btn" onClick={() => setImporting(true)}>
            {t('import.openBtn')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setEditing({ ...EMPTY_FORM })}
          >
            {t('common.addBtn')}
          </button>
        </>
      }
    >
      {isError ? <ErrorState error={error} /> : null}
      {isLoading ? (
        <SkeletonRows rows={5} />
      ) : clients.length === 0 ? (
        <EmptyState>{search ? t('common.noMatches') : t('clients.noneYet')}</EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[12px]">
            <thead>
              <tr className="text-[10.5px] uppercase tracking-[0.03em] text-dim">
                <th className="px-2 py-1.5 font-bold">{t('clients.colName')}</th>
                <th className="px-2 py-1.5 font-bold">{t('clients.colRegCode')}</th>
                <th className="px-2 py-1.5 font-bold">{t('clients.colContact')}</th>
                <th className="px-2 py-1.5 font-bold">{t('clients.colSites')}</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr key={client.client_id} className="border-t border-line">
                  <td className="px-2 py-2 font-semibold">
                    {client.client_name}
                    {client.active ? null : <ArchivedBadge deletedAt={client.deleted_at} />}
                    {/* Measured, not assumed: archiving the client cuts the
                        portal (resolve_client_session re-reads `active` on
                        every request) and leaves the site tracking pages
                        answering 200, because /api/track/<key> authenticates
                        on the site's own portal_access_key and delete_client
                        archives the client row alone. A dispatcher who
                        believes archiving shut every door has been misled by
                        the product, so the row says which one is still open
                        and where to close it. Only where there is one. */}
                    {!client.active && client.site_count > 0 ? (
                      <div className="mt-0.5 text-[11px] font-normal leading-snug text-amber">
                        {t('clients.archivedSitesWarn')}
                      </div>
                    ) : null}
                    {/* Only the ADDRESS, and deliberately not the VAT code.
                        The address is a requisite on every invoice; a missing
                        PVM code may simply be a company that has none, and
                        flagging that would be this product making a claim
                        about somebody else's tax status — the very thing
                        core/pdf_export.py refuses to do by printing an
                        em-dash. An archived client is exempt: nothing new is
                        being invoiced to them. */}
                    {client.active && !client.address ? (
                      <div className="mt-0.5 text-[11px] font-normal leading-snug text-amber">
                        {t('clients.missingInvoiceAddress')}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-2 py-2 text-dim">{client.registration_code || '—'}</td>
                  <td className="px-2 py-2 text-dim">
                    <div className="truncate">{client.contact_phone || '—'}</div>
                    <div className="truncate">{client.contact_email || ''}</div>
                  </td>
                  <td className="px-2 py-2">
                    {client.site_count}
                    {client.pending_request_count > 0 ? (
                      <span className="ml-2 rounded-full bg-amber/15 px-2 py-0.5 text-[9.5px] font-bold uppercase text-amber">
                        {t('clients.pendingN', { n: client.pending_request_count })}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-2 py-2">
                    {/* An archived client gets one action and no others.
                        Issuing a sign-in link for one produces a link that
                        can never be exchanged — core/client_auth.py's
                        exchange_magic_link() selects on active=1 — so the
                        button would hand a dispatcher a URL to send that
                        answers "invalid or expired" at the client's end. */}
                    {client.active ? (
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          className="btn !px-2.5 !py-1 !text-[11px]"
                          onClick={() => {
                            issueLink.reset();
                            revokeSessions.reset();
                            setIssuingFor(client);
                          }}
                        >
                          {t('clients.accessBtn')}
                        </button>
                        <button
                          type="button"
                          className="btn !px-2.5 !py-1 !text-[11px]"
                          onClick={() =>
                            setEditing({
                              clientId: client.client_id,
                              client_name: client.client_name ?? '',
                              registration_code: client.registration_code ?? '',
                          vat_code: client.vat_code ?? '',
                          address: client.address ?? '',
                              contact_email: client.contact_email ?? '',
                              contact_phone: client.contact_phone ?? '',
                              segment: client.segment ?? '',
                              dwell_free_minutes_site:
                                client.dwell_free_minutes_site === null
                                || client.dwell_free_minutes_site === undefined
                                  ? ''
                                  : String(client.dwell_free_minutes_site),
                            })
                          }
                        >
                          {t('common.editBtn')}
                        </button>
                        <ConfirmButton
                          label={t('clients.archiveBtn')}
                          confirmLabel={t('common.confirmDelete')}
                          hint={t('clients.archiveHint')}
                          disabled={archiveClient.isPending}
                          onConfirm={() => archiveClient.mutate({ clientId: client.client_id })}
                        />
                      </div>
                    ) : (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          className="btn !px-2.5 !py-1 !text-[11px]"
                          disabled={restoreClient.isPending}
                          onClick={() => restoreClient.mutate({ clientId: client.client_id })}
                        >
                          {t('common.restoreBtn')}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {archiveClient.isError ? <ErrorState error={archiveClient.error} /> : null}
      {restoreClient.isError ? <ErrorState error={restoreClient.error} /> : null}

      <PaginationFooter
        total={totalCount}
        limit={limit}
        offset={offset}
        onOffsetChange={setOffset}
        onLimitChange={setLimit}
      />

      <ClientAccessModal
        client={issuingFor}
        issue={issueLink}
        revoke={revokeSessions}
        onClose={() => setIssuingFor(null)}
      />

      <SmartImportModal
        open={importing}
        onClose={() => setImporting(false)}
        entityType="client"
        title={t('import.titleClients')}
      />

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={t(editing?.clientId ? 'clients.edit' : 'clients.add')}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn-primary" onClick={submit} disabled={saving}>
              {t('common.saveBtn')}
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-3">
            <Field label={t('clients.colName')}>
              <input
                className="field"
                value={editing.client_name}
                onChange={(e) => setEditing({ ...editing, client_name: e.target.value })}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('clients.colRegCode')}>
                <input
                  className="field"
                  value={editing.registration_code}
                  onChange={(e) => setEditing({ ...editing, registration_code: e.target.value })}
                  placeholder="302456789"
                  inputMode="numeric"
                />
              </Field>
              {/* Not marked required, and the hint says why: a company below
                  the registration threshold has no PVM code and never will,
                  exactly as core/pdf_export.py reasons about the SELLER's.
                  An empty box here is a real state, not an unfinished form. */}
              <Field label={t('clients.vatCodeLabel')}>
                <input
                  className="field"
                  value={editing.vat_code}
                  onChange={(e) => setEditing({ ...editing, vat_code: e.target.value })}
                  placeholder="LT302456789"
                />
                <span className="mt-1 block text-[10px] leading-snug text-dim">
                  {t('clients.vatCodeHint')}
                </span>
              </Field>
            </div>
            {/* The one requisite that is required whichever kind of invoice
                this client receives — see core/pdf_export.py's note on
                80 str.: the address is on its own list, and the company code
                identifies the entity when there is no PVM code to do it.

                Searchable, and the search IS the box rather than a second
                one above it: what a carrier types stays exactly as typed —
                a registered address is a legal string, not a map pin, and
                plenty of them are what somebody's accountant wrote — while
                picking a suggestion fills in the rest of a street, city and
                postcode nobody wants to retype forty times. Coordinates
                come back with the suggestion and are dropped on the floor
                here; there is nowhere on this table to put them and nothing
                that would read them (contrast CoordinateField.jsx, where
                they are the entire point). */}
            <Field label={t('clients.addressLabel')}>
              <AddressSearchInput
                value={editing.address}
                onValueChange={(address) => setEditing({ ...editing, address })}
                placeholder="Ozo g. 5, Vilnius"
              />
              <span className="mt-1 block text-[10px] leading-snug text-dim">
                {t('clients.addressHint')}
              </span>
            </Field>
            <Field label={t('clients.segmentLabel')}>
              <select
                className="field"
                value={editing.segment}
                onChange={(e) => setEditing({ ...editing, segment: e.target.value })}
              >
                <option value="">{t('clients.segmentNone')}</option>
                {CLIENT_SEGMENTS.map((segment) => (
                  <option key={segment} value={segment}>{t(`clients.segment.${segment}`)}</option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('clients.colPhone')}>
                <input
                  className="field"
                  value={editing.contact_phone}
                  onChange={(e) => setEditing({ ...editing, contact_phone: e.target.value })}
                  placeholder="+3706..."
                />
              </Field>
              <Field label={t('clients.colEmail')}>
                <input
                  className="field"
                  value={editing.contact_email}
                  onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })}
                />
              </Field>
              {/* The free unloading time agreed with THIS client. Left empty,
                  the carrier's own setting applies — which is what every
                  client got before, whether or not it matched their
                  contract. */}
              {editing.clientId ? (
                <Field label={t('clients.dwellFreeLabel')}>
                  <input
                    className="field"
                    type="number"
                    min="0"
                    inputMode="numeric"
                    placeholder={t('data.dwellFreeInherit')}
                    value={editing.dwell_free_minutes_site}
                    onChange={(e) => setEditing({ ...editing, dwell_free_minutes_site: e.target.value })}
                  />
                  <span className="mt-1 block text-[10px] leading-snug text-dim">
                    {t('clients.dwellFreeHint')}
                  </span>
                </Field>
              ) : null}
            </div>
            {formError ? <ErrorState error={new Error(t(formError))} /> : null}
            {saveError ? <ErrorState error={saveError} /> : null}
          </div>
        ) : null}
      </Modal>
    </Panel>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">
        {label}
      </span>
      {children}
    </label>
  );
}
